import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useOrderSocket } from "./useOrderSocket";

// ONE shared shift state for Restaurant Panel and Vista. The server is authoritative:
// every figure below comes from /shifts/active; the client never computes money.
export function useShift(rid) {
  const [shift, setShift] = useState(undefined); // undefined = loading, null = no active shift
  const [paymentMethod, setPaymentMethod] = useState("BOTH");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/restaurant/${rid}/shifts/active`);
      setShift(data.shift || null);
      setPaymentMethod(data.payment_method || "BOTH");
    } catch {
      setShift(null);
    }
  }, [rid]);

  useEffect(() => { setShift(undefined); load(); }, [rid, load]);

  // Realtime: shift start/end and each finalized sale push a fresh snapshot.
  useOrderSocket((msg) => {
    if (msg.restaurant_id !== rid && msg.order?.restaurant_id !== rid) return;
    if (msg.type === "shift_update") {
      const s = msg.shift;
      setShift(s && s.status === "OPEN" ? s : null);
      return;
    }
    if (msg.type === "settings_update" || msg.type === "order_update") load();
  }, { restaurantId: rid, onReady: load });

  const start = async (openingCash) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurant/${rid}/shifts`, {
        opening_cash_amount: openingCash === "" || openingCash === null ? 0 : Number(openingCash),
      });
      setShift(data.shift);
      setPaymentMethod(data.payment_method || paymentMethod);
      return { ok: true, created: data.created };
    } catch (e) {
      return { ok: false, error: errText(e) };
    } finally { setBusy(false); }
  };

  const end = async (counted) => {
    if (!shift) return { ok: false, error: "Nema aktivne smene" };
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurant/${rid}/shifts/${shift.id}/end`, {
        closing_cash_counted: counted === "" || counted === null ? null : Number(counted),
      });
      setShift(null);
      return { ok: true, closed: data.shift };
    } catch (e) {
      await load();
      return { ok: false, error: errText(e) };
    } finally { setBusy(false); }
  };

  return { shift, paymentMethod, busy, start, end, reload: load };
}

export function errText(e) {
  const d = e?.response?.data?.detail;
  return (typeof d === "object" ? d.message : d) || "Greška";
}

export function fmtTime(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Belgrade",
  }).format(new Date(iso));
}

export function fmtDuration(fromIso, toIso) {
  if (!fromIso) return "—";
  const ms = (toIso ? new Date(toIso) : new Date()) - new Date(fromIso);
  const m = Math.max(0, Math.floor(ms / 60000));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}h ${String(m % 60).padStart(2, "0")}min`;
}

// Live clock for the running-duration label (1 min granularity).
export function useMinuteTick() {
  const [, setT] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setT((x) => x + 1), 30000);
    return () => clearInterval(i);
  }, []);
}
